import React from 'react';
import { Shield, Lock, FileText, Database, UserCheck, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SEOHead, { ADVISOR_NAME, ADVISOR_PHONE_DISPLAY, ADVISOR_EMAIL } from '../components/SEOHead';

const PrivacyPolicy = () => {
  const { i18n } = useTranslation();
  const isTe = i18n.language === 'te';

  return (
    <div className="min-h-screen pt-24 pb-20 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={isTe ? 'గోప్యతా విధానం | VSR Insurance Plus' : 'Privacy Policy | VSR Insurance Plus'}
        description={isTe ? 'భారతీయ సమాచార సాంకేతిక చట్టం మరియు DPDP చట్ట నిబంధనల ప్రకారం VSR Insurance Plus గోప్యతా విధానం, లీడ్ డేటా నిర్వహణ మరియు కస్టమర్ హక్కులు.' : 'Privacy policy and data protection commitments for VSR Insurance Plus in compliance with Indian Information Technology Act and DPDP Act guidelines.'}
        canonicalPath="/privacy"
        breadcrumbs={[{ name: isTe ? 'గోప్యతా విధానం' : 'Privacy Policy', path: '/privacy' }]}
      />

      <div className="container mx-auto px-6 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 p-2 px-4 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <Lock className="w-3.5 h-3.5" /> {isTe ? 'డేటా భద్రత & గోప్యత' : 'Data Protection & Confidentiality'}
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-4 tracking-tight">
            {isTe ? 'గోప్యతా విధానం' : 'Privacy Policy'}
          </h1>
          <p className="text-sm text-slate-500">
            {isTe ? 'చివరిగా నవీకరించబడింది: అక్టోబర్ 2026' : 'Last Updated & Effective Date: October 2026'}
          </p>
        </div>

        <div className="bg-white dark:bg-slate-900 p-8 md:p-12 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-8 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
          {/* Section 1 */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" /> {isTe ? '1. కస్టమర్ గోప్యత పట్ల మా నిబద్ధత' : '1. Commitment to Client Confidentiality'}
            </h2>
            <p>
              {isTe
                ? 'విఎస్ఆర్ ఇన్సూరెన్స్ ప్లస్ (ఎస్వీఆర్ ఎల్ఐసి సర్వీసెస్, సీనియర్ సలహాదారు వి. ఎస్. రెడ్డి ఆధ్వర్యంలో) మీ వ్యక్తిగత సమాచార భద్రతకు అత్యధిక ప్రాధాన్యతనిస్తుంది. బీమా సలహా ప్రక్రియలో ఆర్థిక లక్ష్యాలు మరియు కుటుంబ వివరాలు ఇమిడి ఉంటాయి కాబట్టి, మేము భారతీయ ఇన్ఫర్మేషన్ టెక్నాలజీ చట్టం 2000 మరియు డిజిటల్ పర్సనల్ డేటా ప్రొటెక్షన్ (DPDP) చట్టం 2023 లకు కట్టుబడి పనిచేస్తాము.'
                : 'At VSR Insurance Plus (operating under SVR LIC Services, guided by Senior Insurance Advisor V. S. Reddy), your privacy is our foundational commitment. Because insurance advisory involves sensitive financial goals, family details, and medical records, we handle all personal information with absolute confidentiality adhering to the Information Technology Act 2000, the SPDI Rules 2011, and the Digital Personal Data Protection (DPDP) Act 2023 of India.'}
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
              {isTe ? '2. మేము సేకరించే సమాచారం' : '2. Information We Collect'}
            </h2>
            <p>
              {isTe
                ? 'మా వెబ్‌సైట్ ఫారమ్‌లు, ఎన్నారై కాల్‌బ్యాక్ అభ్యర్థనలు లేదా ఫోన్ సంప్రదింపుల ద్వారా మీరు స్వచ్ఛందంగా అందించే ప్రాథమిక సంప్రదింపు వివరాలను మాత్రమే సేకరిస్తాము:'
                : 'We collect personal information strictly when voluntarily submitted through our consultation request forms, NRI callback forms, or phone inquiries:'}
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1 text-xs sm:text-sm">
              <li>{isTe ? 'పూర్తి పేరు మరియు దేశం' : 'Full Name and Country of Residence'}</li>
              <li>{isTe ? 'ఫోన్ / వాట్సాప్ నంబర్ (దేశం కోడ్‌తో సహా)' : 'Phone / WhatsApp Number with country code'}</li>
              <li>{isTe ? 'ఆసక్తి ఉన్న బీమా విభాగం (తల్లిదండ్రుల హెల్త్, లైఫ్, పెన్షన్)' : 'Area of Insurance Interest (e.g., Health Insurance for Parents, NRI Term Life)'}</li>
              <li>{isTe ? 'కాల్‌బ్యాక్ మాట్లాడటానికి అనువైన సమయం మరియు సందేశం' : 'Preferred Callback Time and optional family inquiries'}</li>
              <li>{isTe ? 'ట్రాఫిక్ సోర్స్ (ఉదా. ఇన్‌స్టాగ్రామ్ లేదా వెబ్‌సైట్ గైడ్)' : 'Referral attribution parameters (such as UTM Campaign or Referral Source)'}</li>
            </ul>
            <p className="mt-2 text-xs text-slate-500">
              {isTe
                ? '*మేము ప్రారంభ విచారణ దశలో ఆధార్, పాన్ లేదా బ్యాంక్ పాస్‌వర్డ్‌ల వంటి సున్నితమైన గుర్తింపు పత్రాలను ఎప్పుడూ అడగము.'
                : '*We never collect Aadhaar numbers, PAN cards, bank passwords, or sensitive medical records through initial web inquiry forms.'}
            </p>
          </section>

          {/* Section 3: Google Sheets & Storage Disclosure */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" /> {isTe ? '3. లీడ్ డేటా నిల్వ & గూగుల్ షీట్స్ వినియోగం' : '3. Lead Storage & Google Services Disclosure'}
            </h2>
            <p>
              {isTe
                ? 'ఎన్నారై కస్టమర్ల విచారణలను సకాలంలో ట్రాక్ చేయడానికి మరియు సలహాదారు సంప్రదింపులను సమన్వయం చేయడానికి, సమర్పించిన సమాచారం మా భద్రమైన సర్వర్ మరియు పాస్‌వర్డ్-రక్షిత గూగుల్ షీట్స్ డేటాబేస్ (NRI Insurance Leads) లో రికార్డ్ చేయబడుతుంది.'
                : 'To ensure timely coordination across international time zones and prevent lost inquiries, consultation requests and NRI callback submissions are recorded in secure, access-restricted databases, including encrypted server-side logs and an authorized Google Sheets workspace maintained under strict two-factor authentication.'}
            </p>
            <p className="mt-2">
              {isTe
                ? 'ఈ సమాచారాన్ని కేవలం లైసెన్స్ పొందిన సలహాదారు వి. ఎస్. రెడ్డి మరియు అధికారిక కార్యాలయ సిబ్బంది మాత్రమే వీక్షించగలరు. పబ్లిక్ వీక్షణకు ఇది ఎట్టి పరిస్థితుల్లోనూ అందుబాటులో ఉండదు.'
                : 'Access to lead records is strictly restricted to Senior Insurance Advisor V. S. Reddy and authorized operational personnel. We never publish or expose submitted inquiries to public visitors, search engines, or third parties.'}
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-500" /> {isTe ? '4. జీరో-స్పామ్ మరియు డేటా విక్రయం నిషేధం' : '4. Zero-Spam & Zero Data-Selling Guarantee'}
            </h2>
            <p>
              {isTe
                ? 'మీ సంప్రదింపు సమాచారాన్ని ఏ విధమైన థర్డ్-పార్టీ టెలిమార్కెటర్లు, లోన్ ఏజెంట్లు లేదా అడ్వర్టైజర్లకు విక్రయించము లేదా అద్దెకు ఇవ్వము. మీ వివరాలు కేవలం మీ బీమా అభ్యర్థనకు సమాధానం ఇవ్వడానికి మాత్రమే ఉపయోగించబడతాయి.'
                : 'We have an absolute zero-tolerance policy against spam and unsolicited telemarketing. We never sell, trade, rent, or lease your personal information to third-party telemarketers, credit card issuers, or loan agencies. Your contact details are used exclusively to evaluate and respond to your insurance requirements.'}
            </p>
          </section>

          {/* Section 5: Data Correction and Deletion */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-rose-500" /> {isTe ? '5. సమాచార సవరణ మరియు తొలగింపు హక్కులు' : '5. Data Access, Correction & Deletion Rights'}
            </h2>
            <p>
              {isTe
                ? 'మీరు సమర్పించిన సమాచారాన్ని సమీక్షించడానికి, సవరించడానికి లేదా మా రికార్డుల నుండి పూర్తిగా తొలగించమని అభ్యర్థించే పూర్తి హక్కు మీకు ఉంది. మీ అభ్యర్థనను మా అధికారిక ఇమెయిల్ ద్వారా పంపవచ్చు.'
                : 'In accordance with Indian data protection laws and international best practices, you hold the right to request access to your submitted inquiry data, request corrections, or ask for complete deletion of your contact records from our databases.'}
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
              {isTe ? '6. గోప్యతా అధికారి సంప్రదింపు వివరాలు' : '6. Privacy Grievance Officer & Contact'}
            </h2>
            <p>
              {isTe ? 'గోప్యతా విధానం లేదా డేటా హక్కుల గురించి ప్రశ్నల కోసం నేరుగా సంప్రదించండి:' : 'For any privacy-related requests, questions, or data deletion inquiries, please contact our designated privacy officer:'}
            </p>
            <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl space-y-1 text-xs mt-2 border border-slate-200 dark:border-slate-800">
              <p><strong>{isTe ? 'అధికారి:' : 'Officer:'}</strong> {ADVISOR_NAME}</p>
              <p><strong>{isTe ? 'సంస్థ:' : 'Agency:'}</strong> VSR Insurance Plus (SVR LIC Services)</p>
              <p><strong>{isTe ? 'హెల్ప్‌లైన్:' : 'Helpline:'}</strong> {ADVISOR_PHONE_DISPLAY}</p>
              <p><strong>{isTe ? 'అధికారిక ఇమెయిల్:' : 'Official Email:'}</strong> <a href={`mailto:${ADVISOR_EMAIL}`} className="text-primary hover:underline">{ADVISOR_EMAIL}</a></p>
              <p><strong>{isTe ? 'చిరునామా:' : 'Address:'}</strong> SVR LIC Services, Dilsukhnagar / Hyderabad Central, Telangana 500060, India</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
